// @givmo/org-console — the Profile page's STATISTICS card (Profile Enrichment PE-WEB15; D-PE-32,
// D-PE-41, D-PE-42): which of Givmo's figures show under the page's header, and in what order.
//
// Pinned at the top of the Page content tab because that is where the figures sit on the public page:
// under the header, above the organization's own blocks (D-PE-41 — never among them, and never
// movable there; the card offers which stats and their order, nothing else).
//
// ══ IT SAVES ON ITS OWN, EXACTLY AS ITS GATE DOES ═══════════════════════════════════════════════
//
// Each switch and each move is saved at once (the host's `onSave` — the page replace with the
// `statistics` list ALONE, so the block draft below is never part of it and never touched), the way its
// gate, the host's visibility setting card, saves: the new list shows at once, nothing
// confirms a save that landed (the control's new state is the confirmation), the controls never dim
// while a save is in flight, and a refused save puts the list back with the server's sentence in a
// toast. Changes made while a save is in flight are saved after it, the latest list only, so the
// list on screen is the list the server ends with. (The alternative — the card joining the tab's one
// Save — is the mockup's open choice for Jonathan.)
//
// ══ THE GATE IS THE HOST'S ══════════════════════════════════════════════════════════════════════
//
// Whether a stat may show at all is not this card's to decide: a dollar figure the organization's
// setting withholds is the host's business (`onBeforeTurnOn`, asked before a stat turns on — resolve
// `false` to leave it off), and the server says, per chosen stat, why the public does not see it
// (`withheld`), which the card states in the host's words. This file names no class and no stat.
//
// A key this build has no words for is never drawn and never dropped (`profile-statistics.ts`).
//
// ══ EACH ROW SHOWS ITS FIGURE (Jonathan, 2026-10-13) ════════════════════════════════════════════
//
// Every row, on the page or not, leads with the figure a person outside the organization sees for it
// now, drawn as a small version of the strip's number, so the row reads like the stat itself
// (its count over its label); a figure that person doesn't see reads "Hidden" in its place, with no value — the
// line under the list says why. The figures are the server's (each catalog row carries its own), never
// the admin's own view.

import { useRef, useState } from 'react';
import { isGivmoError } from '@givmo/api-client';
import { Card, CardBody, Icon, IconButton, Switch, testIdProp, toast } from '@givmo/design-system';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { PROFILE_COPY } from './profile-copy';
import { isStaleSaveRefusal } from './profile-model';
import {
  STATISTIC_WITHHELD_REASONS,
  formatDefinitionValue,
  joinLabels,
  moveKey,
  shownKeys,
  toggleKey,
  type OwnerProfileStatistics,
  type ProfileStatisticDefinition,
} from './profile-statistics';

const COPY = PROFILE_COPY.statistics;

/** The host's words for its class's stats — rendered verbatim, never branched on. */
export interface ProfileStatisticsVocab {
  /** Each stat's public word — the label the apps draw under the figure ("Donations"). */
  labels: Readonly<Record<string, string>>;
  /** One line per stat saying what the figure counts, for this card. */
  descriptions: Readonly<Record<string, string>>;
  /**
   * The card's note for the chosen stats the public does not see for one reason the server names,
   * given their joined labels ("Donated and Matched") and how many — ONE note per reason under the
   * list, never the same sentence on every row. A reason with no words draws nothing.
   */
  withheld: Readonly<Record<string, (labels: string, count: number) => string>>;
  /** View as public's note for the same stats, from the public's side. */
  withheldPreview?: Readonly<Record<string, (labels: string, count: number) => string>>;
}

export interface ProfileStatisticsCardProps {
  statistics: OwnerProfileStatistics;
  canEdit: boolean;
  vocab: ProfileStatisticsVocab;
  /** Save the new choice (the shown keys, in order; `[]` = every stat off). Rejects with the refusal. */
  onSave: (keys: string[]) => Promise<void>;
  /**
   * The Statistics choice changed after this page was read, so the save was refused (PE-WEB18): the
   * host reads the page again, and the card then shows the latest choice.
   */
  onStale?: () => void;
  /** Asked before a stat turns on — the host's gate. Resolve `false` to leave it off. */
  onBeforeTurnOn?: (stat: ProfileStatisticDefinition) => Promise<boolean>;
  /** A host action drawn after one reason's note (e.g. a door to change the setting behind it). */
  withheldAction?: (reason: string, stats: readonly ProfileStatisticDefinition[]) => ReactNode;
  testIdPrefix: string;
}

export function ProfileStatisticsCard({
  statistics,
  canEdit,
  vocab,
  onSave,
  onStale,
  onBeforeTurnOn,
  withheldAction,
  testIdPrefix,
}: ProfileStatisticsCardProps) {
  const prefix = `${testIdPrefix}-statistics`;
  // The list on screen: the server's, or — while a save is in flight — the latest one asked for.
  const [pending, setPending] = useState<string[] | null>(null);
  // The latest list waiting to be saved, and whether a save is running: saves never overlap, and
  // the one that runs after another sends the latest list only.
  const wanted = useRef<string[] | null>(null);
  const saving = useRef(false);

  const served = shownKeys(statistics);
  const shown = pending ?? served;
  const byKey = new Map(statistics.available.map((stat) => [stat.key, stat]));
  const drawn = (key: string) => byKey.has(key) && vocab.labels[key] !== undefined;
  const shownDrawn = shown.filter(drawn);
  const hidden = statistics.available.filter(
    (stat) => drawn(stat.key) && !shown.includes(stat.key),
  );
  const editable = canEdit;
  // One note per reason, naming that reason's stats in the order they show (the server's reasons for
  // the SERVED list: while a save is in flight the notes wait for the answer rather than guess).
  const notes = pending === null ? withheldNotes(statistics, shownDrawn, byKey, vocab) : [];
  // One figure column for every row, as wide as the widest figure, so the labels line up.
  const figureWidth = figureColumnWidth(statistics.available.filter((stat) => drawn(stat.key)));

  const save = async (next: string[]) => {
    wanted.current = next;
    setPending(next);
    if (saving.current) return;
    saving.current = true;
    try {
      while (wanted.current !== null) {
        const keys = wanted.current;
        wanted.current = null;
        await onSave(keys);
      }
    } catch (refusal) {
      // The host's visibility setting card's grammar: the server's own sentence in a toast, the list put
      // back (the served one, which the refused save never replaced). A choice that changed after this
      // page was read is the one exception (PE-WEB18): the latest choice is shown at once, and the toast
      // says why the change didn't stick — the person's change and the other one touched the same list,
      // so the latest wins (D-PE-45's rule for one part).
      wanted.current = null;
      if (isStaleSaveRefusal(refusal)) {
        toast.error(COPY.errors.stale);
        onStale?.();
      } else {
        toast.error(isGivmoError(refusal) ? refusal.message : COPY.errors.save);
      }
    } finally {
      saving.current = false;
      setPending(null);
    }
  };

  const turn = async (stat: ProfileStatisticDefinition, on: boolean) => {
    if (on && onBeforeTurnOn && !(await onBeforeTurnOn(stat))) return;
    // Built from the list on screen — the one being saved, if a save is in flight.
    await save(toggleKey(wanted.current ?? shown, stat.key, on));
  };

  return (
    <Card id="statistics" className="scroll-mt-[120px]" testId={prefix}>
      <CardBody flush className="flex flex-col">
        <div className="px-20 pt-16 pb-12">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {COPY.heading}
          </h2>
          <p className="mt-2 max-w-[720px] font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
            {COPY.lede}
          </p>
          {!canEdit && (
            <p
              className="mt-8 font-sans text-s13 tracking-givmo text-text-tertiary"
              {...testIdProp(`${prefix}-read-only`)}
            >
              {COPY.readOnly}
            </p>
          )}
        </div>

        {shownDrawn.length === 0 ? (
          <p
            className="border-t border-border-subtle px-20 py-14 font-sans text-s14 tracking-givmo text-text-secondary"
            {...testIdProp(`${prefix}-all-off`)}
          >
            {COPY.allOff}
          </p>
        ) : (
          <ol aria-label={COPY.heading} {...testIdProp(`${prefix}-shown`)}>
            {shownDrawn.map((key, index) => {
              const stat = byKey.get(key)!;
              return (
                <StatisticRow
                  key={key}
                  stat={stat}
                  label={vocab.labels[key]!}
                  description={vocab.descriptions[key]}
                  on
                  editable={editable}
                  figureWidth={figureWidth}
                  first={index === 0}
                  last={index === shownDrawn.length - 1}
                  onToggle={(on) => void turn(stat, on)}
                  onMove={
                    canEdit
                      ? (delta) => void save(moveKey(wanted.current ?? shown, key, delta, drawn))
                      : undefined
                  }
                  prefix={prefix}
                />
              );
            })}
          </ol>
        )}

        {notes.length > 0 && (
          <div
            className="flex flex-col gap-8 border-t border-border-subtle px-20 py-14"
            {...testIdProp(`${prefix}-notes`)}
          >
            {notes.map((note) => (
              <p
                key={note.reason}
                className="flex flex-wrap items-baseline gap-x-8 gap-y-2 font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${prefix}-note-${note.reason}`)}
              >
                <span>{note.text}</span>
                {withheldAction ? withheldAction(note.reason, note.stats) : null}
              </p>
            ))}
          </div>
        )}

        {hidden.length > 0 && (
          <div {...testIdProp(`${prefix}-hidden`)}>
            <p className="border-t border-border-subtle px-20 pt-14 pb-2 font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">
              {COPY.notShown}
            </p>
            <ul aria-label={COPY.notShown}>
              {hidden.map((stat, index) => (
                <StatisticRow
                  key={stat.key}
                  stat={stat}
                  label={vocab.labels[stat.key]!}
                  description={vocab.descriptions[stat.key]}
                  on={false}
                  editable={editable}
                  figureWidth={figureWidth}
                  quiet={index === 0}
                  onToggle={(on) => void turn(stat, on)}
                  prefix={prefix}
                />
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function StatisticRow({
  stat,
  label,
  description,
  on,
  editable,
  figureWidth,
  first = false,
  last = false,
  quiet = false,
  onToggle,
  onMove,
  prefix,
}: {
  stat: ProfileStatisticDefinition;
  label: string;
  description: string | undefined;
  on: boolean;
  editable: boolean;
  /** The figure column's width, or `null` when no row has a figure to show (an older server). */
  figureWidth: string | null;
  first?: boolean;
  last?: boolean;
  /** The first not-shown row: no rule above it (its group's heading carries one). */
  quiet?: boolean;
  onToggle: (on: boolean) => void;
  onMove?: (delta: -1 | 1) => void;
  prefix: string;
}) {
  const id = `${prefix}-row-${stat.key}`;
  const value = formatDefinitionValue(stat);
  return (
    <li
      className={
        quiet
          ? 'flex items-center gap-12 px-20 py-12'
          : 'flex items-center gap-12 border-t border-border-subtle px-20 py-12'
      }
      data-stat-key={stat.key}
      data-shown={on ? 'true' : 'false'}
      {...testIdProp(id)}
    >
      <div
        className={
          figureWidth === null
            ? 'min-w-0 flex-1'
            : 'grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-12'
        }
      >
        {figureWidth !== null && (
          // The strip's number, small: the figure the public sees, or "Hidden" in its place.
          <span
            className={
              on
                ? 'font-sans text-s18 font-w700 tracking-givmo text-text-strong tabular-nums'
                : 'font-sans text-s18 font-w700 tracking-givmo text-text-secondary tabular-nums'
            }
            style={{ minWidth: figureWidth }}
            {...testIdProp(`${id}-figure`)}
          >
            {value ??
              (stat.withheld !== null ? (
                <span className="text-s14 font-w600 text-text-tertiary">{COPY.hidden}</span>
              ) : null)}
          </span>
        )}
        <p
          id={`${id}-label`}
          className={
            on
              ? 'font-sans text-s14 font-w600 tracking-givmo text-text-strong'
              : 'font-sans text-s14 font-w600 tracking-givmo text-text-secondary'
          }
        >
          {label}
        </p>
        {description && (
          <p
            className={
              figureWidth === null
                ? 'mt-2 font-sans text-s13 leading-h15 tracking-givmo text-text-tertiary'
                : 'col-span-2 mt-2 font-sans text-s13 leading-h15 tracking-givmo text-text-tertiary sm:col-span-1 sm:col-start-2'
            }
          >
            {description}
          </p>
        )}
      </div>
      {onMove && (
        <div className="flex shrink-0 items-center gap-2">
          <IconButton
            aria-label={COPY.moveUp(label)}
            size="sm"
            disabled={!editable || first}
            onClick={() => onMove(-1)}
            testId={`${id}-up`}
          >
            <Icon icon={ArrowUp} size={16} aria-hidden />
          </IconButton>
          <IconButton
            aria-label={COPY.moveDown(label)}
            size="sm"
            disabled={!editable || last}
            onClick={() => onMove(1)}
            testId={`${id}-down`}
          >
            <Icon icon={ArrowDown} size={16} aria-hidden />
          </IconButton>
        </div>
      )}
      <Switch
        appearance="saas"
        checked={on}
        disabled={!editable}
        onCheckedChange={(next) => onToggle(next)}
        aria-label={COPY.show(label)}
        testId={`${id}-switch`}
      />
    </li>
  );
}

/**
 * The figure column's width in the figure's own font, as wide as the widest figure a row shows ("Hidden"
 * is drawn smaller, so it counts for less), or `null` when no row has one.
 */
function figureColumnWidth(stats: readonly ProfileStatisticDefinition[]): string | null {
  const widths = stats.map((stat) => {
    const value = formatDefinitionValue(stat);
    if (value !== null) return value.length;
    return stat.withheld !== null ? Math.ceil(COPY.hidden.length * 0.8) : 0;
  });
  const widest = Math.max(0, ...widths);
  return widest === 0 ? null : `${widest}ch`;
}

/** The card's notes: per reason the server names (in its vocabulary's order), the drawn stats it withholds. */
function withheldNotes(
  statistics: OwnerProfileStatistics,
  shownDrawn: readonly string[],
  byKey: ReadonlyMap<string, ProfileStatisticDefinition>,
  vocab: ProfileStatisticsVocab,
): { reason: string; text: string; stats: ProfileStatisticDefinition[] }[] {
  const reasonOf = new Map(statistics.withheld.map((row) => [row.key, row.reason]));
  const reasons = [
    ...STATISTIC_WITHHELD_REASONS,
    ...statistics.withheld
      .map((row) => row.reason)
      .filter((reason) => !(STATISTIC_WITHHELD_REASONS as readonly string[]).includes(reason)),
  ];
  const notes: { reason: string; text: string; stats: ProfileStatisticDefinition[] }[] = [];
  for (const reason of new Set(reasons)) {
    const words = vocab.withheld[reason];
    const keys = shownDrawn.filter((key) => reasonOf.get(key) === reason);
    if (words === undefined || keys.length === 0) continue;
    notes.push({
      reason,
      text: words(joinLabels(keys.map((key) => vocab.labels[key]!)), keys.length),
      stats: keys.map((key) => byKey.get(key)!),
    });
  }
  return notes;
}
